import { deliveryRpc, type DeliveryRow } from './deliveries';

export interface HandoverSelection {
  saleId: string;
  saleNumber: string;
  saleUpdatedAt: string;
  deliveryVersion: number | null;
}
export interface HandoverRow {
  saleId: string;
  saleNumber: string;
  saleUpdatedAt: string;
  deliveryVersion: number;
  customerName: string | null;
  customerPhone: string | null;
  courierName: string;
  trackingNumber: string;
  stage: 'packed' | 'dispatched';
}
export interface HandoverIssue {
  saleNumber: string;
  message: string;
}
export class HandoverPreparationError extends Error {
  constructor(public issues: HandoverIssue[]) {
    super('No worksheet was prepared. Review the deliveries listed below.');
    this.name = 'HandoverPreparationError';
  }
}
export interface HandoverGroup {
  courier: string;
  rows: HandoverRow[];
}
export interface HandoverPage extends HandoverGroup {
  groupRecords: number;
  groupPage: number;
  groupPages: number;
  start: number;
}
export interface HandoverDuplicate {
  courier: string;
  tracking: string;
  saleNumbers: string[];
}

export function freezeHandoverSelection(rows: readonly DeliveryRow[]): HandoverSelection[] {
  const selection = rows.map((row) => ({
    saleId: row.sale_id,
    saleNumber: row.sale_number,
    saleUpdatedAt: row.sale_updated_at,
    deliveryVersion: row.version,
  }));
  validateHandoverSelection(selection);
  return selection;
}
function validateHandoverSelection(selection: readonly HandoverSelection[]) {
  if (selection.length < 1 || selection.length > 100)
    throw new Error('Select 1 to 100 deliveries for a courier handover.');
  if (
    selection.some((row) => !row.saleId || !row.saleUpdatedAt) ||
    new Set(selection.map((row) => row.saleId)).size !== selection.length
  )
    throw new Error(
      'The selection is incomplete or contains duplicate sales. Refresh and select again.',
    );
}
function projectHandoverRow(row: DeliveryRow, expected: HandoverSelection): HandoverRow {
  if (row.sale_id !== expected.saleId)
    throw new Error('Delivery unavailable. Refresh and select again.');
  if (row.fulfillment_method !== 'courier')
    throw new Error('Only Courier shipments belong on this worksheet.');
  if (row.stage !== 'packed' && row.stage !== 'dispatched')
    throw new Error('Choose a Packed or Dispatched shipment.');
  if (!['booked', 'confirmed', 'delivery_pending'].includes(row.sale_status))
    throw new Error('This sale is not ready for a courier handover.');
  if (row.packing_requires_review) throw new Error('Recheck the packed items and address first.');
  if (row.issue) throw new Error('Review and resolve the delivery issue first.');
  if (!row.courier_name?.trim() || !row.tracking_number?.trim())
    throw new Error('Save the courier company and tracking number first.');
  if (!Number.isSafeInteger(row.version) || row.version! <= 0 || !row.sale_updated_at)
    throw new Error('Saved delivery details are incomplete. Refresh and try again.');
  // Both reads use the same server timestamp contract; keep sub-millisecond precision.
  if (row.version !== expected.deliveryVersion || row.sale_updated_at !== expected.saleUpdatedAt)
    throw new Error(
      'This delivery changed. Close, refresh and reselect it before preparing again.',
    );
  return {
    saleId: row.sale_id,
    saleNumber: row.sale_number,
    saleUpdatedAt: row.sale_updated_at,
    deliveryVersion: row.version!,
    customerName: row.customer_name,
    customerPhone: row.customer_mobile,
    courierName: row.courier_name.trim(),
    trackingNumber: row.tracking_number,
    stage: row.stage,
  };
}
function checkAbort(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Preparation cancelled.', 'AbortError');
}
export async function loadHandoverSelection(
  selection: readonly HandoverSelection[],
  signal: AbortSignal,
  onProgress: (done: number, total: number) => void = () => {},
): Promise<HandoverRow[]> {
  validateHandoverSelection(selection);
  checkAbort(signal);
  const output: HandoverRow[] = new Array(selection.length);
  const issues: (HandoverIssue | undefined)[] = new Array(selection.length);
  let next = 0,
    done = 0;
  onProgress(0, selection.length);
  await Promise.all(
    Array.from({ length: Math.min(4, selection.length) }, async () => {
      while (next < selection.length) {
        checkAbort(signal);
        const index = next++;
        const expected = selection[index]!;
        try {
          const row = await deliveryRpc<DeliveryRow>(
            'get_sale_delivery',
            { p_sale_id: expected.saleId },
            signal,
          );
          checkAbort(signal);
          output[index] = projectHandoverRow(row, expected);
        } catch (error) {
          checkAbort(signal);
          const message =
            error instanceof Error
              ? error.message
              : 'Delivery unavailable. Refresh or check access.';
          issues[index] = { saleNumber: expected.saleNumber, message };
        }
        onProgress(++done, selection.length);
      }
    }),
  );
  checkAbort(signal);
  const failures = issues.filter((item): item is HandoverIssue => !!item);
  if (failures.length) throw new HandoverPreparationError(failures);
  return output;
}
export function handoverSelectionFromRows(rows: readonly HandoverRow[]): HandoverSelection[] {
  return rows.map((row) => ({
    saleId: row.saleId,
    saleNumber: row.saleNumber,
    saleUpdatedAt: row.saleUpdatedAt,
    deliveryVersion: row.deliveryVersion,
  }));
}
export function groupHandoverRows(rows: readonly HandoverRow[]): HandoverGroup[] {
  const groups = new Map<string, HandoverGroup>();
  rows.forEach((row) => {
    let group = groups.get(row.courierName);
    if (!group) {
      group = { courier: row.courierName, rows: [] };
      groups.set(row.courierName, group);
    }
    group.rows.push(row);
  });
  return [...groups.values()];
}
export function handoverDuplicates(groups: readonly HandoverGroup[]): HandoverDuplicate[] {
  return groups.flatMap((group) => {
    const refs = new Map<string, string[]>();
    group.rows.forEach((row) =>
      refs.set(row.trackingNumber, [...(refs.get(row.trackingNumber) || []), row.saleNumber]),
    );
    return [...refs.entries()]
      .filter(([, sales]) => sales.length > 1)
      .map(([tracking, saleNumbers]) => ({ courier: group.courier, tracking, saleNumbers }));
  });
}
/** Heights come from the actual final-width DOM, including wrapped rows. */
export function paginateHandoverGroup(
  group: HandoverGroup,
  heights: readonly number[],
  available: number,
): HandoverPage[] {
  if (heights.length !== group.rows.length || !Number.isFinite(available) || available <= 0)
    throw new Error(
      `The header or writing area for ${group.courier} does not fit A4. Use brand-only output or check the saved sender details.`,
    );
  const pages: HandoverPage[] = [];
  let rows: HandoverRow[] = [],
    height = 0,
    start = 0;
  heights.forEach((rowHeight, index) => {
    if (!Number.isFinite(rowHeight) || rowHeight <= 0 || rowHeight > available)
      throw new Error(
        `${group.rows[index]!.saleNumber}: this row does not fit A4 at a readable size. Review the long customer or courier details.`,
      );
    if (rows.length && height + rowHeight > available) {
      pages.push({
        courier: group.courier,
        rows,
        groupRecords: group.rows.length,
        groupPage: 0,
        groupPages: 0,
        start,
      });
      start = index;
      rows = [];
      height = 0;
    }
    rows.push(group.rows[index]!);
    height += rowHeight;
  });
  if (rows.length)
    pages.push({
      courier: group.courier,
      rows,
      groupRecords: group.rows.length,
      groupPage: 0,
      groupPages: 0,
      start,
    });
  return pages.map((page, index) => ({ ...page, groupPage: index + 1, groupPages: pages.length }));
}
